export interface CSVData {
  [key: string]: string | number;
}

export const parseCSV = (csvContent: string): CSVData[] => {
  const lines = csvContent.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
  const data: CSVData[] = [];

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;

    const row: CSVData = {};
    let currentPosition = 0;
    let fieldValue = '';
    let inQuotes = false;
    let fieldIndex = 0;
    const line = lines[i];

    while (currentPosition < line.length && fieldIndex < headers.length) {
      const currentChar = line[currentPosition];

      if (currentChar === '"') {
        if (!inQuotes) {
          // Start of quoted field
          inQuotes = true;
        } else if (currentPosition + 1 < line.length && line[currentPosition + 1] === '"') {
          // Escaped quote inside quoted field
          fieldValue += '"';
          currentPosition++;
        } else {
          // End of quoted field
          inQuotes = false;
        }
      } else if (currentChar === ',' && !inQuotes) {
        // End of field
        const header = headers[fieldIndex];
        // Clean up the field value
        const cleanValue = fieldValue.replace(/^"*|"*$/g, '').replace(/""/g, '"');
        
        // Try to convert to number if appropriate
        const numValue = Number(cleanValue);
        if (!cleanValue.includes('"') && !cleanValue.includes(',') && !isNaN(numValue)) {
          row[header] = numValue;
        } else {
          row[header] = cleanValue;
        }
        
        fieldValue = '';
        fieldIndex++;
      } else {
        fieldValue += currentChar;
      }
      currentPosition++;
    }

    // Handle the last field
    if (fieldIndex < headers.length) {
      const header = headers[fieldIndex];
      // Clean up the last field value
      const cleanValue = fieldValue.replace(/^"*|"*$/g, '').replace(/""/g, '"');
      
      // Try to convert to number if appropriate
      const numValue = Number(cleanValue);
      if (!cleanValue.includes('"') && !cleanValue.includes(',') && !isNaN(numValue)) {
        row[header] = numValue;
      } else {
        row[header] = cleanValue;
      }
    }

    // Filter out rows with invalid data
    const hasValidData = Object.values(row).some(value => value !== undefined && value !== '');
    if (hasValidData) {
      // Clean up any remaining escaped quotes in string values
      Object.keys(row).forEach(key => {
        if (typeof row[key] === 'string') {
          row[key] = (row[key] as string).replace(/\\"/g, '"').replace(/"{2,}/g, '"');
        }
      });
      data.push(row);
    }
  }

  return data;
};

export const parseSummaryCSV = (csvContent: string): { [key: string]: number } => {
  const data = parseCSV(csvContent);
  const summary: { [key: string]: number } = {
    successful_requests: 0,
    failed_requests: 0,
    total_requests: 0
  };
  
  // Aggregate data across all rows
  data.forEach(row => {
    summary.successful_requests += Number(row.successful_requests) || 0;
    summary.failed_requests += Number(row.failed_requests) || 0;
    summary.total_requests += Number(row.total_requests) || 0;
  });
  
  return summary;
};

export const parseFailureBreakdown = (csvContent: string): { [key: string]: number } => {
  const data = parseCSV(csvContent);
  const breakdown: { [key: string]: number } = {};
  
  // Create breakdown by sub_product
  data.forEach(row => {
    const subProduct = row.sub_product as string;
    const failedRequests = Number(row.failed_requests) || 0;
    
    if (subProduct && failedRequests > 0) {
      breakdown[subProduct] = failedRequests;
    }
  });
  
  return breakdown;
}; 